// Free account system (no payments). Health records live server-side,
// owned by user_id and enforced by the backend — never in plain localStorage.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Non-sensitive module progress (game levels, session counts) mirrored to the
// signed-in account so it follows the user across devices.
const STORE_KEYS = {
  health: "silentscan_v11_health_v1",
  neurorehab: "silentscan_neurorehab_v1",
  neurospeak: "silentscan_neurospeak_v1",
  neuromotor: "silentscan_neuromotor_v1",
};

const readKey = (k) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const localStores = () => {
  const stores = {};
  for (const [name, key] of Object.entries(STORE_KEYS)) {
    const v = readKey(key);
    if (v) stores[name] = v;
  }
  return stores;
};

const pushStores = async () => {
  const stores = localStores();
  if (!Object.keys(stores).length) return;
  await axios.post(`${API}/user/sync`, { stores }, { withCredentials: true });
};

const pullStores = async () => {
  const { data } = await axios.get(`${API}/user/data`, { withCredentials: true });
  return data;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me when returning from OAuth — AuthCallback exchanges first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const googleLogin = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/history";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/signup`, { name, email, password }, { withCredentials: true });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    setUser(null);
    toast.success("Signed out");
  }, []);

  // After login: upload this device's module progress; if the device is fresh
  // and the account has progress, restore it locally.
  const afterAuth = useCallback(async () => {
    try {
      const local = localStores();
      const cloud = await pullStores();
      if (Object.keys(local).length) {
        await pushStores();
      } else if (cloud?.has_data) {
        for (const [name, key] of Object.entries(STORE_KEYS)) {
          if (cloud.stores?.[name]) {
            try {
              localStorage.setItem(key, JSON.stringify(cloud.stores[name]));
            } catch {
              /* ignore */
            }
          }
        }
        toast.success("Your saved progress was restored to this device");
      }
    } catch (e) {
      console.warn("post-auth sync failed", e);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        authOpen,
        openAuth: () => setAuthOpen(true),
        closeAuth: () => setAuthOpen(false),
        googleLogin,
        login,
        signup,
        logout,
        afterAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
